import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { LetDirective } from '@ngrx/component';
import { PorscheDesignSystemModule } from '@porsche-design-system/components-angular';
import {
  BehaviorSubject,
  distinctUntilChanged,
  type Observable,
  ReplaySubject,
  shareReplay,
  tap,
} from 'rxjs';
import type { WorkPosition } from '../../shared/models/api/work-position.model';
import { WorkPositionService } from '../../shared/services/api/work-position.service';
import { MediaQueryService } from '../../shared/services/media-query.service';
import { WorkPositionCreateComponent } from './work-position-create/work-position-create.component';
import { WorkPositionDeleteComponent } from './work-position-delete/work-position-delete.component';
import { WorkPositionEditComponent } from './work-position-edit/work-position-edit.component';

@Component({
  selector: 'opa-work-position-list',
  imports: [
    PorscheDesignSystemModule,
    CommonModule,
    LetDirective,
    WorkPositionCreateComponent,
    WorkPositionDeleteComponent,
    WorkPositionEditComponent,
  ],
  templateUrl: './work-position-list.component.html',
  styleUrl: './work-position-list.component.scss',
})
export class WorkPositionListComponent {
  readonly mediaQueryService = inject(MediaQueryService);
  private readonly positionService = inject(WorkPositionService);

  private readonly loading = new BehaviorSubject(false);
  private readonly position = new ReplaySubject<WorkPosition | null>(1);
  readonly mediaMaxXs$ = this.mediaQueryService.max('xs');
  readonly loading$ = this.loading.pipe(distinctUntilChanged());
  readonly position$ = this.position.asObservable();

  data$ = this.getData$();
  showCreateModal = false;
  showDeleteModal = false;
  showEditModal = false;

  trackPosition(index: number, position: WorkPosition): number {
    return position.id;
  }

  reloadData(): void {
    this.data$ = this.getData$();
  }

  private getData$(): Observable<WorkPosition[]> {
    this.loading.next(true);

    return this.positionService.getWorkPositions().pipe(
      tap({
        next: () => this.loading.next(false),
        error: () => this.loading.next(false),
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  openDeleteModal(position: WorkPosition): void {
    this.position.next(position);
    this.showDeleteModal = true;
  }

  closeDeleteModal(position?: WorkPosition): void {
    this.showDeleteModal = false;
    if (position) this.reloadData();
    this.position.next(null);
  }

  openEditModal(position: WorkPosition): void {
    this.position.next(position);
    this.showEditModal = true;
  }

  closeEditModal(success?: boolean): void {
    this.showEditModal = false;
    if (success) this.reloadData();
    this.position.next(null);
  }
}
